/**
 * Quick Buy - overlay trigger and modal loader for product cards
 */

import { ThemeBus } from './events-bus.js';
import { EVENTS } from './event-constants.js';
import { updateCartBubble } from './utilities.js';
import './variant-selector.js?v=20260917d';

const MODAL_ID = 'quick-buy-modal';
const SECTION_ID = 'quick-buy-modal';
const PRIMARY_IMAGE_WIDTHS = [290, 580, 870];

function resizeShopifyImage(url, width) {
  if (!url) return '';
  const decoded = url.replace(/&amp;/g, '&');
  if (/([?&])width=\d+/.test(decoded)) {
    return decoded.replace(/([?&])width=\d+/, `$1width=${width}`);
  }
  return `${decoded}${decoded.includes('?') ? '&' : '?'}width=${width}`;
}

function getVariantMediaSrc(variant) {
  const media = variant?.featured_media;
  if (!media) return null;
  if (typeof media === 'string') return media;
  return media.preview_image?.src || media.src || null;
}

class QuickBuyController {
  #abortController = null;
  #isLoading = false;

  constructor() {
    this.modalBody = document.querySelector('[data-quick-buy-modal-body]');
    this.loadingEl = document.querySelector('[data-quick-buy-loading]');

    document.addEventListener('click', this.#handleDocumentClick);
    ThemeBus.addEventListener(EVENTS.modalClose, this.#handleModalClose);
    ThemeBus.addEventListener(EVENTS.cartItemAdded, this.#handleCartItemAdded);
    ThemeBus.addEventListener(EVENTS.gridUpdated, this.#handleGridUpdated);
    ThemeBus.addEventListener(EVENTS.variantChange, this.#handleVariantChange);
  }

  #isModalOpen() {
    const modal = document.querySelector(`[data-modal-id="${MODAL_ID}"]`);
    return Boolean(
      modal &&
        !modal.classList.contains('hidden') &&
        this.modalBody?.querySelector('[data-quick-buy-content]')
    );
  }

  #handleVariantChange = (event) => {
    if (!this.#isModalOpen()) return;

    const variant = event.detail?.variant;
    if (!variant) return;

    const content = this.modalBody.querySelector('[data-quick-buy-content]');
    const src = getVariantMediaSrc(variant) || content?.dataset.fallbackImage || null;
    if (!src) return;

    this.#updatePrimaryImages(src);
  };

  #updatePrimaryImages(src) {
    const images = this.modalBody.querySelectorAll('[data-quick-buy-primary] img');
    if (!images.length) return;

    const src1x = resizeShopifyImage(src, PRIMARY_IMAGE_WIDTHS[0]);
    const srcset = PRIMARY_IMAGE_WIDTHS.map(
      (width) => `${resizeShopifyImage(src, width)} ${width}w`
    ).join(', ');

    images.forEach((img) => {
      img.src = src1x;
      img.srcset = srcset;
      if (img.hasAttribute('data-src')) img.setAttribute('data-src', src1x);
      if (img.hasAttribute('data-srcset')) img.setAttribute('data-srcset', srcset);
    });
  }

  #handleCartItemAdded = () => {
    const modal = document.querySelector(`[data-modal-id="${MODAL_ID}"]`);
    if (!modal || modal.classList.contains('hidden')) return;
    if (!this.modalBody?.querySelector('quick-add-component')) return;

    ThemeBus.dispatchEvent(new CustomEvent(EVENTS.modalClose, { detail: { id: MODAL_ID } }));
  };

  #handleGridUpdated = () => {
    // Grid re-renders keep their triggers; no action needed unless we want to rebind.
  };

  #handleModalClose = (event) => {
    if (event.detail?.id !== MODAL_ID) return;

    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }

    this.#isLoading = false;
    this.#setLoading(false);
    this.#clearModalContent();
  };

  #handleDocumentClick = async (event) => {
    const trigger = event.target.closest('[data-quick-buy-trigger]');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();

    const productHandle = trigger.dataset.productHandle;
    const variantId = trigger.dataset.variantId;
    const singleVariant = trigger.dataset.singleVariant === 'true';
    const forceModal = trigger.dataset.forceModal === 'true';

    if (!productHandle) return;

    if (singleVariant && !forceModal && variantId) {
      await this.#directAddToCart(variantId, 1, trigger);
      return;
    }

    await this.#openModal(productHandle, variantId);
  };

  #setLoading(loading) {
    if (!this.loadingEl) return;
    this.loadingEl.classList.toggle('hidden', !loading);
  }

  #clearModalContent() {
    if (!this.modalBody) return;
    this.modalBody.querySelector('#product-json')?.remove();
    this.modalBody.querySelector('[data-quick-buy-content]')?.remove();
  }

  async #directAddToCart(variantId, quantity, trigger) {
    const btn = trigger;
    const originalDisabled = btn.disabled;

    btn.disabled = true;
    btn.classList.add('opacity-50');

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);
      const vendor = trigger?.dataset.productVendor;
      if (vendor) formData.append('properties[Vendor]', vendor);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      const result = await response.json();

      if (result.status && result.status !== 200) {
        console.error('[QuickBuy] Add to cart failed:', result.message);
        return;
      }

      await this.#updateCartData();
      ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
      ThemeBus.dispatchEvent(new Event(EVENTS.cartItemAdded));
    } catch (error) {
      console.error('[QuickBuy] Error adding to cart:', error);
    } finally {
      btn.disabled = originalDisabled;
      btn.classList.remove('opacity-50');
    }
  }

  async #updateCartData() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      window.__cart = await response.json();
      updateCartBubble();
    } catch (error) {
      console.error('[QuickBuy] Error fetching cart:', error);
    }
  }

  async #openModal(productHandle, variantId) {
    if (!this.modalBody || this.#isLoading) return;

    this.#isLoading = true;
    this.#clearModalContent();
    this.#setLoading(true);

    ThemeBus.dispatchEvent(new CustomEvent(EVENTS.modalOpen, { detail: { id: MODAL_ID } }));

    if (this.#abortController) {
      this.#abortController.abort();
    }
    this.#abortController = new AbortController();

    try {
      const url = new URL(`/products/${productHandle}`, window.location.origin);
      url.searchParams.set('section_id', SECTION_ID);
      if (variantId) {
        url.searchParams.set('variant', variantId);
      }

      const response = await fetch(url.toString(), { signal: this.#abortController.signal });
      if (!response.ok) {
        throw new Error(`Failed to load quick buy modal: ${response.status}`);
      }

      const html = await response.text();
      this.#injectContent(html);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[QuickBuy] Modal load failed:', error);
      ThemeBus.dispatchEvent(new CustomEvent(EVENTS.modalClose, { detail: { id: MODAL_ID } }));
    } finally {
      this.#isLoading = false;
      this.#setLoading(false);
      this.#abortController = null;
    }
  }

  #injectContent(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = doc.querySelector('[data-quick-buy-content]');

    if (!content) {
      throw new Error('Quick buy content not found in section response');
    }

    document.getElementById('product-json')?.remove();

    const productJson = doc.getElementById('product-json');
    if (productJson) {
      const script = document.createElement('script');
      script.id = 'product-json';
      script.type = 'application/json';
      script.textContent = productJson.textContent;
      this.modalBody.appendChild(script);
    }

    const contentClone = content.cloneNode(true);
    contentClone.querySelector('#product-json')?.remove();
    this.modalBody.appendChild(contentClone);
  }
}

function initQuickBuy() {
  if (!document.querySelector('[data-quick-buy-modal-body]')) return;
  new QuickBuyController();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuickBuy);
} else {
  initQuickBuy();
}

export { initQuickBuy };
